import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import type { SendCredentialsResult, Tag, Test, User, WhatsAppStatus } from "@/types";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";

type Mode = "personal" | "public";
type Channel = "email" | "whatsapp" | "manual";

export default function SendCredentials() {
  const { id = "" } = useParams<{ id: string }>();

  const [test, setTest] = useState<Test | null>(null);
  const [contacts, setContacts] = useState<User[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [waStatus, setWaStatus] = useState<WhatsAppStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Selezione destinatari
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());

  // Opzioni invio
  const [mode, setMode] = useState<Mode>("personal");
  const [channels, setChannels] = useState<Set<Channel>>(new Set(["email"]));
  const [regenerate, setRegenerate] = useState(false);

  // Risultato
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendCredentialsResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<Test>(`/admin/tests/${id}`),
      api.get<User[]>("/admin/students"),
      api.get<Tag[]>("/admin/tags"),
      api.get<WhatsAppStatus>("/admin/whatsapp/status").catch(() => null),
    ])
      .then(([t, c, tgs, wa]) => {
        setTest(t); setContacts(c); setTags(tgs);
        setWaStatus(wa);
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-disattiva mode=public se il test non è pubblicoAnonimo
  useEffect(() => {
    if (test && !test.pubblicoAnonimo && mode === "public") {
      setMode("personal");
    }
  }, [test, mode]);

  // Calcola destinatari effettivi (union tag ∪ singoli, filtro attivi)
  const effectiveRecipients = useMemo(() => {
    const ids = new Set<string>(selectedContacts);
    for (const tagId of selectedTags) {
      for (const c of contacts) {
        if (c.tags?.some((t) => t.id === tagId)) ids.add(c.id);
      }
    }
    return contacts.filter((c) => ids.has(c.id) && c.active);
  }, [contacts, selectedContacts, selectedTags]);

  const toggleContact = (id: string) => {
    const next = new Set(selectedContacts);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedContacts(next);
  };
  const toggleTag = (id: string) => {
    const next = new Set(selectedTags);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedTags(next);
  };
  const toggleChannel = (c: Channel) => {
    const next = new Set(channels);
    if (next.has(c)) next.delete(c); else next.add(c);
    setChannels(next);
  };

  async function send() {
    if (effectiveRecipients.length === 0) {
      setError("Seleziona almeno un destinatario");
      return;
    }
    if (channels.size === 0) {
      setError("Seleziona almeno un canale");
      return;
    }
    setSending(true); setError(null); setResult(null);
    try {
      const r = await api.post<SendCredentialsResult>(
        `/admin/tests/${id}/send-credentials`,
        {
          contactIds: Array.from(selectedContacts),
          tagIds: Array.from(selectedTags),
          mode,
          channels: Array.from(channels),
          regenerate,
        },
      );
      setResult(r);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Spinner size={32} /></div>;
  if (!test) return <div className="p-10 text-center text-red-300">Test non trovato.</div>;

  const recipientsWithoutPhone = effectiveRecipients.filter((c) => !c.telefono).length;
  const isWhatsAppReady = waStatus?.state === "connected";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-5">
      <header className="flex justify-between items-baseline">
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-secondary">
            Invio credenziali
          </div>
          <h1 className="text-2xl font-serif text-primary">{test.titolo}</h1>
        </div>
        <Link to="/admin/tests" className="font-mono text-xs text-secondary hover:text-primary">
          ← test
        </Link>
      </header>

      {/* STEP 1 — Destinatari */}
      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
          1. Destinatari ({effectiveRecipients.length} effettivi)
        </h2>

        {tags.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-mono text-secondary mb-1.5">Per tag</div>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => {
                const sel = selectedTags.has(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleTag(t.id)}
                    style={sel ? { backgroundColor: t.colore, borderColor: t.colore } : { borderColor: t.colore, color: t.colore }}
                    className={`text-xs font-mono px-2 py-1 rounded border ${sel ? "text-white" : ""}`}>
                    {sel ? "✓ " : ""}{t.nome} ({t.nContatti ?? 0})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs font-mono text-secondary mb-1.5">Singoli contatti</div>
        <div className="max-h-60 overflow-y-auto space-y-1 bg-bg rounded-lg p-2">
          {contacts.filter((c) => c.active).map((c) => {
            const fromTag = c.tags?.some((t) => selectedTags.has(t.id));
            const label = (c.nome || c.cognome)
              ? `${c.nome ?? ""} ${c.cognome ?? ""}`.trim()
              : c.email;
            return (
              <label key={c.id} className={`flex items-center gap-2 p-1.5 hover:bg-card rounded cursor-pointer ${fromTag ? "opacity-60" : ""}`}>
                <input
                  type="checkbox"
                  checked={selectedContacts.has(c.id) || fromTag}
                  disabled={fromTag}
                  onChange={() => toggleContact(c.id)}
                />
                <span className="text-primary text-sm">{label}</span>
                {!c.telefono && <span className="text-xs text-muted font-mono">(no tel)</span>}
                {fromTag && <span className="text-xs text-secondary font-mono">via tag</span>}
                <span className="text-xs text-muted font-mono ml-auto">{c.email}</span>
              </label>
            );
          })}
        </div>
      </Card>

      {/* STEP 2 — Modalità link */}
      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
          2. Modalità link
        </h2>
        <div className="space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="radio" checked={mode === "personal"} onChange={() => setMode("personal")} className="mt-1" />
            <div>
              <div className="text-primary text-sm">Personale per ogni contatto</div>
              <div className="text-xs text-secondary">URL univoco e tracciato per ognuno. Apre il test già autenticato come quel contatto.</div>
            </div>
          </label>
          <label className={`flex items-start gap-2 ${test.pubblicoAnonimo ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
            <input type="radio" checked={mode === "public"} onChange={() => setMode("public")}
              disabled={!test.pubblicoAnonimo} className="mt-1" />
            <div>
              <div className="text-primary text-sm">Link pubblico unico</div>
              <div className="text-xs text-secondary">
                Stesso URL per tutti, anonimo. Allo start lo studente inserisce nome/cognome.
                {!test.pubblicoAnonimo && " ⚠ Disponibile solo per test con flag 'pubblicoAnonimo'."}
              </div>
            </div>
          </label>
          {mode === "personal" && (
            <label className="flex items-center gap-2 text-xs text-secondary mt-2 cursor-pointer">
              <input type="checkbox" checked={regenerate} onChange={(e) => setRegenerate(e.target.checked)} />
              Rigenera il token anche per chi ha già un assignment (invalida i link precedenti)
            </label>
          )}
        </div>
      </Card>

      {/* STEP 3 — Canale */}
      <Card>
        <h2 className="font-mono text-xs uppercase tracking-wider text-secondary mb-3">
          3. Canale di invio (puoi sceglierne più di uno)
        </h2>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={channels.has("email")} onChange={() => toggleChannel("email")} />
            <span className="text-primary text-sm">📧 Email (richiede SMTP configurato)</span>
          </label>
          <label className={`flex items-center gap-2 ${isWhatsAppReady ? "cursor-pointer" : "opacity-50"}`}>
            <input type="checkbox" checked={channels.has("whatsapp")} onChange={() => toggleChannel("whatsapp")}
              disabled={!isWhatsAppReady} />
            <span className="text-primary text-sm">
              💬 WhatsApp via Baileys
              {!isWhatsAppReady && (
                <span className="text-xs text-secondary ml-2">
                  (non connesso —{" "}
                  <Link to="/admin/whatsapp" className="text-accent underline">collega qui</Link>)
                </span>
              )}
              {recipientsWithoutPhone > 0 && channels.has("whatsapp") && (
                <span className="text-xs text-accent ml-2">⚠ {recipientsWithoutPhone} senza telefono saranno saltati</span>
              )}
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={channels.has("manual")} onChange={() => toggleChannel("manual")} />
            <span className="text-primary text-sm">📋 Solo genera (li copio e mando a mano)</span>
          </label>
        </div>
      </Card>

      {/* Bottone invio */}
      <div className="flex items-center gap-3">
        <Button onClick={send} loading={sending} disabled={effectiveRecipients.length === 0}>
          Invia a {effectiveRecipients.length} destinatari →
        </Button>
        {error && <span className="text-red-300 text-sm">{error}</span>}
      </div>

      {/* Risultato */}
      {result && (
        <Card className="border-accent space-y-3">
          <h3 className="font-mono text-xs uppercase tracking-wider text-accent">
            Risultato — {result.destinatari} destinatari
          </h3>

          {result.publicUrl && (
            <div className="bg-bg rounded-lg p-3">
              <div className="text-xs font-mono text-secondary mb-1">Link pubblico:</div>
              <div className="flex gap-2">
                <input readOnly value={result.publicUrl} onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 bg-card border border-border rounded-lg p-2 text-primary font-mono text-xs" />
                <button onClick={() => navigator.clipboard.writeText(result.publicUrl!)}
                  className="bg-accent text-white px-3 py-2 rounded-lg font-mono text-xs">Copia</button>
              </div>
            </div>
          )}

          {result.perCanale.email && (
            <div className="text-sm">
              <Badge color="#2C5F8A">email</Badge>
              <span className="ml-2 text-secondary">
                tentati {result.perCanale.email.tentati} ·{" "}
                <span className="text-green">inviati {result.perCanale.email.inviati}</span> ·{" "}
                <span className="text-red-300">falliti {result.perCanale.email.falliti}</span>
              </span>
            </div>
          )}

          {result.perCanale.whatsapp && (
            <div className="text-sm">
              <Badge color="#2A7A4B">whatsapp</Badge>
              {result.perCanale.whatsapp.motivo ? (
                <span className="ml-2 text-accent">{result.perCanale.whatsapp.motivo}</span>
              ) : (
                <span className="ml-2 text-secondary">
                  tentati {result.perCanale.whatsapp.tentati} ·{" "}
                  <span className="text-green">inviati {result.perCanale.whatsapp.inviati}</span> ·{" "}
                  <span className="text-red-300">falliti {result.perCanale.whatsapp.falliti}</span>
                </span>
              )}
            </div>
          )}

          {result.perCanale.manual && result.perCanale.manual.length > 0 && (
            <div className="space-y-2">
              <Badge color="#5B3E8A">manuale</Badge>
              <div className="bg-bg rounded-lg p-3 max-h-80 overflow-y-auto space-y-1.5">
                {result.perCanale.manual.map((m) => (
                  <div key={m.contactId} className="flex items-center gap-2 py-1 border-b border-border last:border-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-primary text-sm">{m.nome || m.email}</div>
                      <input readOnly value={m.url} onFocus={(e) => e.currentTarget.select()}
                        className="w-full bg-card border border-border rounded p-1 text-xs font-mono text-secondary" />
                    </div>
                    <button onClick={() => navigator.clipboard.writeText(m.url)}
                      className="bg-card border border-border text-primary px-2 py-1 rounded font-mono text-xs hover:border-accent">
                      Copia
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.errori.length > 0 && (
            <div>
              <div className="text-xs font-mono text-red-300 mb-1">Errori dettaglio:</div>
              <ul className="text-xs text-red-300 font-mono space-y-0.5">
                {result.errori.map((e, i) => <li key={i}>· {e}</li>)}
              </ul>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
